/**
 * Zod validation schemas for Sales types
 */

import { z } from 'zod';
import type {
  SaleResponse,
  SaleItemResponse,
  CreateSaleRequest,
  CreateSaleItemRequest,
  UpdateSaleRequest,
  UpdateSaleStatusRequest,
  SaleBreakdown,
  DiscountApplication,
  TaxSummaryBreakdown,
} from '@kisanlink/erp-service';

export const taxSummaryBreakdownSchema = z.object({
  cgst_amount: z.number().nonnegative(),
  sgst_amount: z.number().nonnegative(),
  igst_amount: z.number().nonnegative(),
  vat_amount: z.number().nonnegative(),
  other_tax_amount: z.number().nonnegative(),
  total_tax_amount: z.number().nonnegative(),
}) satisfies z.ZodType<TaxSummaryBreakdown>;

export const discountApplicationSchema = z.object({
  discount_id: z.string(),
  discount_name: z.string(),
  discount_code: z.string(),
  discount_type: z.string(),
  amount: z.number().nonnegative(),
  applied_by: z.enum(['manual', 'coupon', 'auto']),
}) satisfies z.ZodType<DiscountApplication>;

export const saleBreakdownSchema = z.object({
  base_amount: z.number().nonnegative(),
  discount_amount: z.number().nonnegative(),
  total_savings: z.number().nonnegative(),
  tax_amount: z.number().nonnegative(),
  final_amount: z.number().nonnegative(),
  applied_discounts: z.array(discountApplicationSchema),
  tax_breakdown: taxSummaryBreakdownSchema,
}) satisfies z.ZodType<SaleBreakdown>;

export const saleItemSchema = z.object({
  id: z.string(),
  sale_id: z.string(),
  batch_id: z.string(),
  sku: z.string().optional(), // NEW: Product variant SKU (fetched from variant through batch)
  quantity: z.number().positive('Quantity must be positive'),
  selling_price: z.number().nonnegative(),
  cost_price: z.number().nonnegative(),
  margin: z.number(),
  line_total: z.number().nonnegative(),
  cgst_amount: z.number().nonnegative(),
  sgst_amount: z.number().nonnegative(),
  custom_tax_amount: z.number().nonnegative(),
  total_tax_amount: z.number().nonnegative(),
  created_at: z.string(),
}) satisfies z.ZodType<SaleItemResponse>;

export const saleSchema = z.object({
  id: z.string(),
  warehouse_id: z.string(),
  customer_phone: z.string().optional().nullable(),
  customer_name: z.string().optional().nullable(),
  is_org_member: z.boolean(),
  sale_date: z.string(),
  sale_type: z.enum(['in_store', 'delivery']),
  payment_mode: z.enum(['cash', 'upi', 'online']),
  status: z.enum(['pending', 'confirmed', 'completed', 'cancelled']).or(z.string()), // Allow string for flexibility but document expected values
  total_amount: z.number().nonnegative(),
  apply_taxes: z.boolean(),
  breakdown: saleBreakdownSchema,
  items: z.array(saleItemSchema),
  created_at: z.string(),
  updated_at: z.string(),
}) satisfies z.ZodType<SaleResponse>;

export const createSaleItemSchema = z.object({
  variant_id: z.string().min(1, 'Variant ID is required'),
  quantity: z.number().positive('Quantity must be positive'),
}) satisfies z.ZodType<CreateSaleItemRequest>;

export const createSaleSchema = z.object({
  warehouse_id: z.string().min(1, 'Warehouse ID is required'),
  customer_phone: z.string().optional().nullable(),
  customer_name: z.string().optional().nullable(),
  is_org_member: z.boolean().optional().default(false),
  sale_date: z.string().optional(),
  sale_type: z.enum(['in_store', 'delivery']),
  payment_mode: z.enum(['cash', 'upi', 'online']),
  items: z.array(createSaleItemSchema).min(1, 'At least one item is required'),
  apply_taxes: z.boolean().optional().default(true), // Default changed from false to true (BREAKING CHANGE)
  auto_apply_discounts: z.boolean().optional().default(true),
  discount_id: z.string().optional(),
  coupon_code: z.string().optional(),
}) satisfies z.ZodType<CreateSaleRequest>;

export const updateSaleSchema = z.object({
  status: z.string().optional(),
}) satisfies z.ZodType<UpdateSaleRequest>;

export const updateSaleStatusSchema = z.object({
  status: z.string().min(1, 'Status is required'),
}) satisfies z.ZodType<UpdateSaleStatusRequest>;

export type Sale = z.infer<typeof saleSchema>;
export type SaleItem = z.infer<typeof saleItemSchema>;
export type CreateSale = z.infer<typeof createSaleSchema>;
export type CreateSaleItem = z.infer<typeof createSaleItemSchema>;
export type UpdateSale = z.infer<typeof updateSaleSchema>;
export type UpdateSaleStatus = z.infer<typeof updateSaleStatusSchema>;
export type SaleBreakdownType = z.infer<typeof saleBreakdownSchema>;
export type DiscountApplicationType = z.infer<typeof discountApplicationSchema>;
export type TaxSummaryBreakdownType = z.infer<typeof taxSummaryBreakdownSchema>;
